import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Link2, MousePointerClick, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { CoverImage } from "@/components/CoverImage";
import { copyText } from "@/components/SmartlinkCard";
import { Input } from "@/components/ui/input";
import { t } from "@/i18n";

export default function SmartlinksPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/smartlinks").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const rows = (items || []).filter((r) => `${r.title} ${r.artist_name}`.toLowerCase().includes(q.toLowerCase()));
  const totals = rows.reduce((a, r) => ({ clicks: a.clicks + (r.smartlink.clicks || 0), unique: a.unique + (r.smartlink.unique_clicks || 0) }), { clicks: 0, unique: 0 });
  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/smartlinks/sync-metrics"); await load(); toast.success(t("Métricas actualizadas ({n} enlaces)", { n: data.updated })); }
    catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t("Promoción")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Smartlinks</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{t("Un enlace por lanzamiento con todas las plataformas. Los pre-save se convierten en página de lanzamiento el día del estreno.")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar…")} className="w-full sm:w-64" data-testid="smartlinks-search-input" />
          <Button variant="outline" onClick={sync} disabled={syncing} className="gap-2 shrink-0" data-testid="smartlinks-sync-button"><RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {t(syncing ? "Actualizando…" : "Actualizar clics")}</Button>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3 max-w-xl" data-testid="smartlinks-totals">
          {[["Enlaces", rows.length], ["Clics", totals.clicks], ["Visitantes únicos", totals.unique]].map(([k, v]) => (
            <div key={k} className="panel p-4"><p className="eyebrow">{t(k)}</p><p className="font-display text-2xl font-bold mt-1">{v}</p></div>
          ))}
        </div>
      )}
      {items && rows.length === 0 && (
        <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="smartlinks-empty">
          <Link2 className="h-6 w-6 mx-auto mb-3 opacity-50" />{t("Todavía no hay smartlinks. Se generan cuando un lanzamiento recibe su UPC.")}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="smartlinks-grid">
        {rows.map((r) => (
          <div key={r.id} className="panel p-4 flex gap-4 rise" data-testid={`smartlink-item-${r.id}`}>
            <CoverImage fileId={r.cover_file_id} className="h-20 w-20 rounded-md shrink-0" />
            <div className="min-w-0 flex-1 space-y-1.5">
              <Link to={`/lanzamientos/${r.id}`} className="font-display font-semibold truncate block hover:text-primary">{r.title}</Link>
              <p className="text-xs text-muted-foreground truncate">{r.artist_name}{r.live_date ? ` · ${r.live_date}` : ""}</p>
              <div className="flex items-center gap-2">
                <span className="inline-block text-[10px] rounded-full border border-border px-2 py-0.5 text-muted-foreground">{t(r.smartlink.type === "presave" ? "Pre-save" : "Lanzamiento")}</span>
                {r.smartlink.clicks !== undefined && <span className="flex items-center gap-1 text-[11px] text-foreground" data-testid={`smartlink-item-clicks-${r.id}`}><MousePointerClick className="h-3 w-3" /> {t("{a} clics · {b} únicos", { a: r.smartlink.clicks, b: r.smartlink.unique_clicks })}</span>}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <a href={r.smartlink.url} target="_blank" rel="noreferrer" className="font-mono text-xs text-primary truncate hover:underline flex-1" data-testid={`smartlink-item-url-${r.id}`}>{r.smartlink.url.replace(/^https?:\/\//, "")}</a>
                <button onClick={() => copyText(r.smartlink.url)} className="text-muted-foreground hover:text-foreground" title={t("Copiar")} data-testid={`smartlink-item-copy-${r.id}`}><Copy className="h-3.5 w-3.5" /></button>
                <a href={r.smartlink.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground" data-testid={`smartlink-item-open-${r.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
